import React, { useState } from 'react';
import JustificationContainer from '~/components/atom/JustificationContainer';
import TextButton from '~/components/atom/TextButton';
import InputWithButtons from '~/components/molecule/InputWithButtons';
import useGlobalKeyBinding from '~/hooks/useGlobalKeyBinding';
import Catalog from '~/Catalog';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import type { RefetchPlansQuery } from '../../types';
import getSignupId from '../../utils/getSignupId';
import {
  removeLocalStorageItem,
  setLocalStorageItem,
  SIGNUP_ID,
} from '~/util/localStorageKeys';

// Url to test signup id: http://localhost:8000/register?signupId=32eb47bb-b6d4-4c6f-b3ac-557b5b5b9a1a
// const DEV_SIGNUP_ID = '32eb47bb-b6d4-4c6f-b3ac-557b5b5b9a1a';

const text = {
  buttonLabel: 'Ik heb een Signup ID',
  inputLabel: 'Signup ID',
  confirmButton: Catalog.confirm,
  emptySignupIdError: 'Het veld kan niet leeg zijn',
  signupIdApplied: 'Signup ID toegepast: ',
};

export type Props = {
  /** Refetch the plans query */
  refetchPlans: RefetchPlansQuery;
};

const UpdateSignupIdButton: React.FCC<Props> = ({ refetchPlans }) => {
  const defaultValue = getSignupId() ?? '';

  const account = useCurrentAccount();
  const [showInput, setShowInput] = useState<boolean>(false);
  const [value, setValue] = useState<string>(defaultValue);

  useGlobalKeyBinding({
    keys: 'enter',
    callback: () => {
      onConfirm();
    },
    enabled: value.length > 0,
  });

  const onConfirm = () => {
    if (value.length === 0) return;

    setLocalStorageItem(SIGNUP_ID, JSON.stringify({ signupId: value }));

    void refetchPlans({
      accountId: account.id,
      signupId: value,
    });
    setShowInput(false);
  };

  const onRemove = () => {
    setShowInput(false);
    setValue('');
    removeLocalStorageItem(SIGNUP_ID);

    // reset query
    if (value.length > 0) {
      void refetchPlans({
        accountId: account.id,
        signupId: null,
      });
    }
  };

  return (
    <JustificationContainer width="100%">
      {showInput ? (
        <InputWithButtons
          buttonActions={[
            {
              icon: 'trashcan',
              appearance: 'danger',
              onClick: onRemove,
              label: 'Verwijder Signup ID',
            },
            {
              icon: 'check',
              appearance: 'success',
              onClick: onConfirm,
              disabled: value === '',
            },
          ]}
          value={value}
          onChange={e => setValue(e.target.value)}
          width="100%"
          label={{ text: text.inputLabel }}
          autoFocus
        />
      ) : (
        <TextButton
          onClick={() => setShowInput(true)}
          label={!value ? text.buttonLabel : `${text.signupIdApplied}${value}`}
          appearance={value ? 'success' : 'primary'}
          icon={value ? 'check' : 'key'}
          padding={[null]}
        />
      )}
    </JustificationContainer>
  );
};

export default UpdateSignupIdButton;
